import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Clock, Tag, ArrowRight, ArrowLeft, User } from 'lucide-react';
import SEO from '../components/SEO';
import { blogPosts, blogPostsArray } from '../data/blogPosts';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const post = blogPosts[slug];

  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = blogPostsArray.filter((p) => p.slug !== slug).slice(0, 2);

  // Schema.org BlogPosting + FAQPage combined
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: post.author,
      url: 'https://www.staffingforce.nl',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Staffing Force',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_staffforce-new-site/artifacts/8pffcrsv_image.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.staffingforce.nl/blog/${post.slug}`,
    },
    keywords: post.keywords.join(', '),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <main className="pt-28 min-h-screen bg-white">
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        image={post.image}
        type="article"
        article={{ publishedTime: post.date, author: post.author }}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2a] via-[#0d1b2a]/70 to-[#0d1b2a]/30" />
        </div>
        <div className="relative max-w-4xl mx-auto w-full px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors min-h-[44px]"
            >
              <ArrowLeft size={16} /> Alle artikelen
            </Link>
            <span className="inline-block bg-[#00897B] text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-5 leading-[1.1]">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-xs md:text-sm">
              <span className="flex items-center gap-2">
                <User size={14} className="text-[#00bfa5]" /> {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={14} className="text-[#00bfa5]" />
                {new Date(post.date).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={14} className="text-[#00bfa5]" /> {post.readTime} leestijd
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium mb-10">
            {post.intro}
          </p>

          {post.sections.map((s, i) => (
            <div key={i} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                {s.heading}
              </h2>
              <p className="text-gray-700 leading-relaxed text-base md:text-lg">{s.body}</p>
              {s.list && (
                <ul className="mt-5 space-y-2 pl-1">
                  {s.list.map((li, li_i) => (
                    <li
                      key={li_i}
                      className="flex items-start gap-3 text-gray-700 text-sm md:text-base"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* FAQ */}
          <div className="mt-14 pt-10 border-t border-gray-200">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Veelgestelde vragen
            </h2>
            <div className="space-y-5">
              {post.faqs.map((f, i) => (
                <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl p-5 md:p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 text-base md:text-lg">
                    {f.q}
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Inline CTA */}
          <div className="mt-14 bg-gradient-to-br from-[#0d1b2a] to-[#00897B] rounded-2xl p-8 md:p-10 text-white text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">{post.cta.title}</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">{post.cta.body}</p>
            <Link
              to={post.cta.link}
              className="inline-flex items-center gap-2 bg-white text-[#00897B] hover:bg-[#00bfa5] hover:text-white px-6 py-3.5 rounded-lg font-semibold text-sm transition-all min-h-[48px]"
            >
              {post.cta.button} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Tags */}
          <div className="mt-10 flex flex-wrap items-center gap-2">
            <Tag size={14} className="text-gray-400" />
            {post.keywords.map((k) => (
              <span
                key={k}
                className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Meer lezen
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.imageAlt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[#00897B] text-xs font-semibold uppercase tracking-wider">
                      {p.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mt-2 group-hover:text-[#00897B] transition-colors leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogDetailPage;
